import React, { createContext, useContext, useMemo } from 'react';
import { useHttpClientCore } from '@/shared';
import { ApiRemediationRepository } from '../repositories/ApiRemediationRepository';
import {
  AddSection1ItemUseCase,
  AssignLocationUseCase,
  AssignRemediationUseCase,
  BulkAssignRemediationUseCase,
  CompleteRemediationUseCase,
  DismissFindingUseCase,
  GetAssignableUsersUseCase,
  EmployeeSubmitUseCase,
  EmployeeIdentityRequirementsUseCase,
  EmployeeVerifyUseCase,
  GetRemediationAuditUseCase,
  GetRemediationVerificationUseCase,
  GetRemediationQueueUseCase,
  GetRemediationRecordUseCase,
  SaveCorrectionUseCase,
  SaveCorrectionsBatchUseCase,
  MarkSection2SignedUseCase,
  RemoveSection1ItemUseCase,
  SaveSection2DocumentUseCase,
  SendToEmployeeUseCase,
  SkipEmployeeUseCase,
  UploadDocumentUseCase,
} from '../../application/use-cases/RemediationUseCases';

interface RemediationDIContextType {
  repository: ApiRemediationRepository;
  getQueueUseCase: GetRemediationQueueUseCase;
  getRecordUseCase: GetRemediationRecordUseCase;
  saveCorrectionUseCase: SaveCorrectionUseCase;
  saveCorrectionsBatchUseCase: SaveCorrectionsBatchUseCase;
  markSection2SignedUseCase: MarkSection2SignedUseCase;
  saveSection2DocumentUseCase: SaveSection2DocumentUseCase;
  uploadDocumentUseCase: UploadDocumentUseCase;
  dismissFindingUseCase: DismissFindingUseCase;
  assignUseCase: AssignRemediationUseCase;
  bulkAssignUseCase: BulkAssignRemediationUseCase;
  sendToEmployeeUseCase: SendToEmployeeUseCase;
  skipEmployeeUseCase: SkipEmployeeUseCase;
  addSection1ItemUseCase: AddSection1ItemUseCase;
  removeSection1ItemUseCase: RemoveSection1ItemUseCase;
  assignLocationUseCase: AssignLocationUseCase;
  completeUseCase: CompleteRemediationUseCase;
  getAuditUseCase: GetRemediationAuditUseCase;
  getVerificationUseCase: GetRemediationVerificationUseCase;
  getAssignableUsersUseCase: GetAssignableUsersUseCase;
  employeeIdentityRequirementsUseCase: EmployeeIdentityRequirementsUseCase;
  employeeVerifyUseCase: EmployeeVerifyUseCase;
  employeeSubmitUseCase: EmployeeSubmitUseCase;
}

const RemediationDIContext = createContext<RemediationDIContextType | undefined>(undefined);

export const RemediationDIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const httpClientCore = useHttpClientCore();

  const value = useMemo<RemediationDIContextType>(() => {
    const repository = new ApiRemediationRepository(httpClientCore);
    return {
      repository,
      getQueueUseCase: new GetRemediationQueueUseCase(repository),
      getRecordUseCase: new GetRemediationRecordUseCase(repository),
      saveCorrectionUseCase: new SaveCorrectionUseCase(repository),
      saveCorrectionsBatchUseCase: new SaveCorrectionsBatchUseCase(repository),
      markSection2SignedUseCase: new MarkSection2SignedUseCase(repository),
      saveSection2DocumentUseCase: new SaveSection2DocumentUseCase(repository),
      uploadDocumentUseCase: new UploadDocumentUseCase(repository),
      dismissFindingUseCase: new DismissFindingUseCase(repository),
      assignUseCase: new AssignRemediationUseCase(repository),
      bulkAssignUseCase: new BulkAssignRemediationUseCase(repository),
      sendToEmployeeUseCase: new SendToEmployeeUseCase(repository),
      skipEmployeeUseCase: new SkipEmployeeUseCase(repository),
      addSection1ItemUseCase: new AddSection1ItemUseCase(repository),
      removeSection1ItemUseCase: new RemoveSection1ItemUseCase(repository),
      assignLocationUseCase: new AssignLocationUseCase(repository),
      completeUseCase: new CompleteRemediationUseCase(repository),
      getAuditUseCase: new GetRemediationAuditUseCase(repository),
      getVerificationUseCase: new GetRemediationVerificationUseCase(repository),
      getAssignableUsersUseCase: new GetAssignableUsersUseCase(repository),
      employeeIdentityRequirementsUseCase: new EmployeeIdentityRequirementsUseCase(repository),
      employeeVerifyUseCase: new EmployeeVerifyUseCase(repository),
      employeeSubmitUseCase: new EmployeeSubmitUseCase(repository),
    };
  }, [httpClientCore]);

  return <RemediationDIContext.Provider value={value}>{children}</RemediationDIContext.Provider>;
};

export const useRemediationDIContext = (): RemediationDIContextType => {
  const context = useContext(RemediationDIContext);
  if (!context) {
    throw new Error('useRemediationDIContext must be used within RemediationDIProvider');
  }
  return context;
};
